import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { ThemeMode } from '../styles/colors';
import { buildTheme } from '../styles/theme';

interface ThemeModeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
}

const STORAGE_KEY = 'mobileShopTheme';

const ThemeModeContext = createContext<ThemeModeContextType>({ mode: 'light', toggleMode: () => undefined });

const readStoredMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
    // No choice made yet: follow whatever the machine is set to.
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

/**
 * Light or dark, remembered per machine.
 *
 * <p>Ctrl+M flips it from anywhere, the same shortcut the retail suite uses, so
 * someone moving between the two apps does not have to learn a second one.
 */
export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(readStoredMode);

  const toggleMode = useCallback(() => {
    setMode((previous) => {
      const next: ThemeMode = previous === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* a locked-down browser just means it is not remembered */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLowerCase() === 'm') {
        event.preventDefault();
        toggleMode();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleMode]);

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);
